import React, {useState} from 'react';
import {createPortal} from 'react-dom';
import {Link, useLocation} from 'react-router-dom';

interface SidebarExportMenuProps {
    isCollapsed: boolean;
}

const SidebarExportMenu: React.FC<SidebarExportMenuProps> = ({isCollapsed}) => {
    const location = useLocation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [flyoutPos, setFlyoutPos] = useState<{ top: number; left: number } | null>(null);

    const menuItems = [
        {id: 'nouveau', title: 'Nouveau Dossier Export', icon: 'bi-file-earmark-plus', path: '/export/nouveau'},
        {id: 'gestion', title: 'Gestion Export', icon: 'bi-grid-3x2-gap', path: '/export/gestion'},
        {id: 'positionnement', title: 'Positionnement', icon: 'bi-geo-alt', path: '/export/positionnement'},
        {id: 'relevage', title: 'Relevage', icon: 'bi-box-arrow-up', path: '/export/relevage'},
        {id: 'recap', title: 'Récap Export', icon: 'bi-table', path: '/recap/export'}

    ];

    const isActive = (path: string) => location.pathname === path;

    const isAnyExportActive = () =>
        location.pathname.startsWith('/export') || location.pathname.startsWith('/dossiers-export') ||
        location.pathname === '/recap/export';

    if (isCollapsed) {
        return (
            <div
                className="relative"
                onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setFlyoutPos({top: rect.top, left: rect.right + 8});
                    setIsExpanded(true);
                }}
                onMouseLeave={() => setIsExpanded(false)}
            >
                {isAnyExportActive() && (
                    <span
                        className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1 h-6 bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.7)] z-10"/>
                )}
                <div
                    className={`group py-3 mx-3 my-0.5 rounded-xl flex items-center justify-center cursor-pointer transition-all duration-200 ${
                        isAnyExportActive()
                            ? 'bg-gradient-to-r from-emerald-400 to-green-500 text-white shadow-md shadow-emerald-900/30'
                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <i className={`bi bi-file-arrow-up text-[17px] transition-transform duration-200 ${isAnyExportActive() ? '' : 'group-hover:scale-110'}`}/>
                </div>

                {isExpanded && flyoutPos && createPortal(
                    <div
                        style={{position: 'fixed', top: flyoutPos.top, left: flyoutPos.left}}
                        onMouseEnter={() => setIsExpanded(true)}
                        onMouseLeave={() => setIsExpanded(false)}
                        className="w-60 rounded-xl bg-green-900/95 backdrop-blur-sm ring-1 ring-white/10 shadow-2xl shadow-black/40 py-2 z-[1100]">
                        <p className="px-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-300/70">Exports</p>
                        {menuItems.map(item => (
                            <Link
                                key={item.id}
                                to={item.path}
                                className={`flex items-center py-2 px-4 mx-2 rounded-lg no-underline text-[12.5px] transition-all duration-200 ${
                                    isActive(item.path)
                                        ? 'bg-white/15 text-white font-medium ring-1 ring-white/10'
                                        : 'text-white/65 hover:bg-white/10 hover:text-white/90'
                                }`}
                            >
                                <i className={`bi ${item.icon} mr-2 text-sm`}/>
                                <span className="flex-1">{item.title}</span>
                                {item.id === 'relever' &&
                                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse ml-1"/>}
                            </Link>
                        ))}
                    </div>,
                    document.body
                )}
            </div>
        );
    }

    return (
        <div className="mb-1.5 relative">
            {isAnyExportActive() && (
                <span
                    className="absolute left-1.5 top-4 -translate-y-1/2 w-1 h-6 bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.7)] z-10"/>
            )}

            <div
                className={`group py-2.5 px-4 mx-3 my-0.5 rounded-xl cursor-pointer flex items-center justify-between transition-all duration-200 ${
                    isAnyExportActive()
                        ? 'bg-gradient-to-r from-emerald-400 to-green-500 text-white shadow-md shadow-emerald-900/30'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center">
                    <i className={`bi bi-box-arrow-up text-[17px] mr-3 transition-transform duration-200 ${isAnyExportActive() ? '' : 'group-hover:scale-110'}`}/>
                    <span className="text-[13px] font-medium tracking-wide">Exports</span>
                </div>
                <i className={`bi bi-chevron-down text-xs transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}/>
            </div>

            <div
                className={`overflow-hidden transition-all duration-300 ease-out ${isExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="mx-3 my-1 rounded-xl bg-black/15 ring-1 ring-white/5 backdrop-blur-sm py-2 space-y-0.5">
                    {menuItems.map(item => (
                        <Link
                            key={item.id}
                            to={item.path}
                            className={`flex items-center py-2 px-3 mx-2 rounded-lg no-underline text-[12.5px] transition-all duration-200 ${
                                isActive(item.path)
                                    ? 'bg-white/15 text-white font-medium ring-1 ring-white/10'
                                    : 'text-white/65 hover:bg-white/10 hover:text-white/90'
                            }`}
                        >
                            <i className={`bi ${item.icon} mr-2 text-sm`}/>
                            <span className="flex-1">{item.title}</span>
                            {item.id === 'relever' &&
                                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"/>}
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SidebarExportMenu;
